/**
 * MotorBox Vehicle Selector
 * Handles vehicle selection with Year → Make → Model → Submodel cascade
 * Stores selection in sessionStorage for persistent filtering
 */

class VehicleSelector {
  constructor() {
    this.storageKey = 'motorbox_selected_vehicle';
    this.selectors = {
      year: document.querySelector('[data-vehicle-year]'),
      make: document.querySelector('[data-vehicle-make]'),
      model: document.querySelector('[data-vehicle-model]'),
      submodel: document.querySelector('[data-vehicle-submodel]')
    };

    this.init();
  }

  init() {
    // Load saved vehicle selection
    this.loadSavedVehicle();

    // Attach event listeners
    if (this.selectors.year) {
      this.selectors.year.addEventListener('change', (e) => this.handleYearChange(e));
    }
    if (this.selectors.make) {
      this.selectors.make.addEventListener('change', (e) => this.handleMakeChange(e));
    }
    if (this.selectors.model) {
      this.selectors.model.addEventListener('change', (e) => this.handleModelChange(e));
    }
    if (this.selectors.submodel) {
      this.selectors.submodel.addEventListener('change', (e) => this.handleSubmodelChange(e));
    }

    // Handle vehicle selector modal
    this.setupModal();

    // Handle clear vehicle button
    const clearBtn = document.querySelector('[data-clear-vehicle]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clearVehicle());
    }
  }

  /**
   * Load saved vehicle from sessionStorage
   */
  loadSavedVehicle() {
    const saved = sessionStorage.getItem(this.storageKey);
    if (saved) {
      try {
        const vehicle = JSON.parse(saved);
        this.displaySavedVehicle(vehicle);
      } catch (e) {
        console.error('Failed to parse saved vehicle:', e);
      }
    }
  }

  /**
   * Display saved vehicle in UI
   */
  displaySavedVehicle(vehicle) {
    const display = document.querySelector('[data-vehicle-display]');
    if (display && vehicle.year && vehicle.make && vehicle.model) {
      const vehicleText = `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.submodel ? ' ' + vehicle.submodel : ''}`;
      display.textContent = vehicleText;
      display.classList.add('has-vehicle');
    }

    // Set select values
    if (this.selectors.year && vehicle.year) {
      this.selectors.year.value = vehicle.year;
    }
    if (this.selectors.make && vehicle.make) {
      this.selectors.make.value = vehicle.make;
    }
    if (this.selectors.model && vehicle.model) {
      this.selectors.model.value = vehicle.model;
    }
    if (this.selectors.submodel && vehicle.submodel) {
      this.selectors.submodel.value = vehicle.submodel;
    }
  }

  /**
   * Save vehicle to sessionStorage
   */
  saveVehicle(vehicle) {
    sessionStorage.setItem(this.storageKey, JSON.stringify(vehicle));
    this.updateProductCompatibility(vehicle);
    this.displaySavedVehicle(vehicle);
  }

  /**
   * Clear vehicle selection
   */
  clearVehicle() {
    sessionStorage.removeItem(this.storageKey);

    // Reset selectors
    Object.values(this.selectors).forEach(selector => {
      if (selector) {
        selector.value = '';
        selector.disabled = false;
      }
    });

    // Reset display
    const display = document.querySelector('[data-vehicle-display]');
    if (display) {
      display.textContent = 'Select Your Vehicle';
      display.classList.remove('has-vehicle');
    }

    // Reload page to reset filters
    if (window.location.pathname.includes('/collections/')) {
      window.location.reload();
    }
  }

  /**
   * Handle year selection
   */
  handleYearChange(e) {
    const year = e.target.value;

    if (!year) {
      this.resetDependentSelects(['make', 'model', 'submodel']);
      return;
    }

    // Enable make selector
    if (this.selectors.make) {
      this.selectors.make.disabled = false;
      this.populateMakes(year);
    }

    this.resetDependentSelects(['model', 'submodel']);
  }

  /**
   * Handle make selection
   */
  handleMakeChange(e) {
    const make = e.target.value;
    const year = this.selectors.year?.value;

    if (!make || !year) {
      this.resetDependentSelects(['model', 'submodel']);
      return;
    }

    // Enable model selector
    if (this.selectors.model) {
      this.selectors.model.disabled = false;
      this.populateModels(year, make);
    }

    this.resetDependentSelects(['submodel']);
  }

  /**
   * Handle model selection
   */
  handleModelChange(e) {
    const model = e.target.value;
    const year = this.selectors.year?.value;
    const make = this.selectors.make?.value;

    if (!model || !year || !make) {
      this.resetDependentSelects(['submodel']);
      return;
    }

    // Enable submodel selector if exists
    if (this.selectors.submodel) {
      this.selectors.submodel.disabled = false;
      this.populateSubmodels(year, make, model);
    }

    // Save vehicle (submodel is optional)
    this.saveVehicle({ year, make, model });
  }

  /**
   * Handle submodel selection
   */
  handleSubmodelChange(e) {
    const submodel = e.target.value;
    const year = this.selectors.year?.value;
    const make = this.selectors.make?.value;
    const model = this.selectors.model?.value;

    if (year && make && model) {
      this.saveVehicle({ year, make, model, submodel });
    }
  }

  /**
   * Reset dependent select elements
   */
  resetDependentSelects(fields) {
    fields.forEach(field => {
      const selector = this.selectors[field];
      if (selector) {
        selector.innerHTML = `<option value="">Select ${field.charAt(0).toUpperCase() + field.slice(1)}</option>`;
        selector.disabled = true;
      }
    });
  }

  /**
   * Populate makes based on year
   * NOTE: This is a placeholder. In production, this would fetch from Shopify metafields
   */
  populateMakes(year) {
    // Placeholder data - replace with actual Shopify data fetch
    const makes = ['Ford', 'Chevrolet', 'Toyota', 'Honda', 'RAM', 'GMC', 'Nissan', 'Jeep'];

    if (this.selectors.make) {
      this.selectors.make.innerHTML = '<option value="">Select Make</option>' +
        makes.map(make => `<option value="${make}">${make}</option>`).join('');
    }
  }

  /**
   * Populate models based on year and make
   * NOTE: This is a placeholder. In production, this would fetch from Shopify metafields
   */
  populateModels(year, make) {
    // Placeholder data - replace with actual Shopify data fetch
    const models = {
      'Ford': ['F-150', 'F-250', 'F-350', 'Ranger', 'Maverick'],
      'Chevrolet': ['Silverado 1500', 'Silverado 2500', 'Colorado', 'Tahoe'],
      'Toyota': ['Tacoma', 'Tundra', '4Runner', 'RAV4'],
      'Honda': ['Ridgeline', 'CR-V', 'Pilot'],
      'RAM': ['1500', '2500', '3500'],
      'GMC': ['Sierra 1500', 'Sierra 2500', 'Canyon'],
      'Nissan': ['Frontier', 'Titan', 'Rogue'],
      'Jeep': ['Gladiator', 'Wrangler', 'Grand Cherokee']
    };

    if (this.selectors.model) {
      const makeModels = models[make] || [];
      this.selectors.model.innerHTML = '<option value="">Select Model</option>' +
        makeModels.map(model => `<option value="${model}">${model}</option>`).join('');
    }
  }

  /**
   * Populate submodels based on year, make, and model
   * NOTE: This is a placeholder. In production, this would fetch from Shopify metafields
   */
  populateSubmodels(year, make, model) {
    // Placeholder data - replace with actual Shopify data fetch
    const submodels = ['Base', 'XLT', 'Lariat', 'King Ranch', 'Platinum', 'Limited'];

    if (this.selectors.submodel) {
      this.selectors.submodel.innerHTML = '<option value="">Select Trim (Optional)</option>' +
        submodels.map(sub => `<option value="${sub}">${sub}</option>`).join('');
    }
  }

  /**
   * Update product compatibility display
   * Supports multiple years, makes, models, submodels via metafields
   */
  updateProductCompatibility(vehicle) {
    const compatibilityElements = document.querySelectorAll('[data-product-compatibility]');

    compatibilityElements.forEach(el => {
      const vehicleText = `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.submodel ? ' ' + vehicle.submodel : ''}`;

      // Check if product has metafield data
      const productMetafields = el.dataset.productMetafields;

      if (productMetafields) {
        try {
          const fitment = JSON.parse(productMetafields);
          const isCompatible = this.checkCompatibility(fitment, vehicle);

          if (isCompatible) {
            el.innerHTML = `
              <div class="product-compatibility product-compatibility--fits">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="var(--color-primary)"/>
                </svg>
                <span>Fits your <strong>${vehicleText}</strong></span>
              </div>
            `;
            el.style.display = 'block';
          } else {
            el.innerHTML = `
              <div class="product-compatibility product-compatibility--no-fit">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="#ff5555"/>
                </svg>
                <span>May not fit your ${vehicleText}</span>
                <button class="product-compatibility__details-btn" onclick="this.nextElementSibling.style.display='block'">View compatible vehicles</button>
                <div class="product-compatibility__details" style="display:none;">
                  ${this.formatCompatibilityDetails(fitment)}
                </div>
              </div>
            `;
            el.style.display = 'block';
          }
        } catch (e) {
          console.error('Error parsing product metafields:', e);
          // Fall back to generic message
          this.showGenericCompatibility(el, vehicleText);
        }
      } else {
        // No metafield data - show generic message
        this.showGenericCompatibility(el, vehicleText);
      }
    });
  }

  /**
   * Show generic compatibility message (no metafield data available)
   */
  showGenericCompatibility(el, vehicleText) {
    el.innerHTML = `
      <div class="product-compatibility">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="var(--color-primary)"/>
        </svg>
        <span>Fits your <strong>${vehicleText}</strong></span>
      </div>
    `;
    el.style.display = 'block';
  }

  /**
   * Check if selected vehicle matches product fitment
   * Supports:
   * - Multiple years (array or range string "2015-2023")
   * - Multiple makes (array)
   * - Multiple models (array)
   * - Multiple submodels (array)
   */
  checkCompatibility(fitment, vehicle) {
    if (!fitment || !vehicle) return false;

    // Check year compatibility
    const yearMatch = this.checkYearCompatibility(fitment.years, vehicle.year);
    if (!yearMatch) return false;

    // Check make compatibility
    const makeMatch = this.checkArrayCompatibility(fitment.makes, vehicle.make);
    if (!makeMatch) return false;

    // Check model compatibility
    const modelMatch = this.checkArrayCompatibility(fitment.models, vehicle.model);
    if (!modelMatch) return false;

    // Check submodel compatibility (optional - matches if "All" or if included)
    if (vehicle.submodel && fitment.submodels && fitment.submodels.length > 0) {
      const submodelMatch = this.checkArrayCompatibility(fitment.submodels, vehicle.submodel);
      if (!submodelMatch) return false;
    }

    return true;
  }

  /**
   * Check if year matches (supports arrays and range strings)
   */
  checkYearCompatibility(years, targetYear) {
    if (!years || years.length === 0) return true; // No restriction
    if (years.includes('All')) return true;

    const target = parseInt(targetYear);

    for (let yearValue of years) {
      // Check for range (e.g., "2015-2023")
      if (typeof yearValue === 'string' && yearValue.includes('-')) {
        const [start, end] = yearValue.split('-').map(y => parseInt(y.trim()));
        if (target >= start && target <= end) {
          return true;
        }
      }
      // Check for exact match
      else if (parseInt(yearValue) === target) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if value is in array (supports "All" wildcard)
   */
  checkArrayCompatibility(array, value) {
    if (!array || array.length === 0) return true; // No restriction
    if (array.includes('All')) return true;
    return array.includes(value);
  }

  /**
   * Format compatibility details for display
   */
  formatCompatibilityDetails(fitment) {
    let html = '<div class="fitment-details">';

    if (fitment.years && fitment.years.length > 0) {
      html += `<div><strong>Years:</strong> ${fitment.years.join(', ')}</div>`;
    }

    if (fitment.makes && fitment.makes.length > 0) {
      html += `<div><strong>Makes:</strong> ${fitment.makes.join(', ')}</div>`;
    }

    if (fitment.models && fitment.models.length > 0) {
      html += `<div><strong>Models:</strong> ${fitment.models.join(', ')}</div>`;
    }

    if (fitment.submodels && fitment.submodels.length > 0) {
      html += `<div><strong>Trims:</strong> ${fitment.submodels.join(', ')}</div>`;
    }

    html += '</div>';
    return html;
  }

  /**
   * Setup modal functionality
   */
  setupModal() {
    const modal = document.querySelector('[data-vehicle-modal]');
    const openBtns = document.querySelectorAll('[data-open-vehicle-selector]');
    const closeBtn = document.querySelector('[data-close-vehicle-modal]');

    if (modal) {
      openBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          modal.classList.add('active');
          document.body.style.overflow = 'hidden';
        });
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          modal.classList.remove('active');
          document.body.style.overflow = '';
        });
      }

      // Close on backdrop click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
    }
  }

  /**
   * Get currently selected vehicle
   */
  getSelectedVehicle() {
    const saved = sessionStorage.getItem(this.storageKey);
    return saved ? JSON.parse(saved) : null;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.vehicleSelector = new VehicleSelector();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VehicleSelector;
}
